import { Typography } from '@material-ui/core'
import React, { useEffect, useRef } from 'react'

import { PromotionErrorCode } from '../../../../helpers/promotion'
import { getTranslate, lang } from '../../../../localization'
import { CartItem } from '../../../../types/cart'
import { AdvertisedPromotion } from '../../../../types/promotion'
import AppliedOffer from './AppliedOffer'
import CardHeading from './CardHeading'
import CodeField, { CODE_FIELD_ID } from './CodeField'
import OfferList from './OfferList'
import useStyles from './styles'
import { useAdvertisedOffers } from './useAdvertisedOffers'
import { usePromotionEvaluation } from './usePromotionEvaluation'
import { verdictMessage } from './verdictMessage'

type PromotionCodeEntryProps = {
  cartID: string
  cartKey?: string
  cartItems?: CartItem[]
  code: string
  disabled: boolean
  /**
   * A TRANSPORT failure from the booking attempt — the promotion service being
   * unreachable, not a code being refused. Business outcomes arrive from the evaluate
   * query below with the service's own wording; this channel exists only for the case
   * where nothing could be evaluated at all.
   */
  errorCode?: PromotionErrorCode
  onChange: (code: string) => void
}

/**
 * Discount code entry, checked WHEN APPLIED.
 *
 * It used to say "the code will be checked when you continue", and it meant it: the code
 * went into makePrimitiveReservation and the booker learned the outcome from that
 * mutation's error path. That path maps gRPC STATUS CODES, which is correct for transport
 * failures and wrong for a business decision — so roughly eighteen distinct, translated
 * reasons arrived as four generic English sentences, of which "expired or not eligible
 * for this stay" covered a wrong property, a blackout date, a day-of-week rule, an
 * advance-purchase window, an exhausted cap and an exhausted budget alike. None of them
 * tells a booker what to do next.
 *
 * evaluatePromotion answers the same question without reserving anything, and returns
 * reasonText already translated. That string is rendered verbatim. Nothing here maps the
 * reason enum to copy, and the four-string table that used to do so is gone.
 *
 * A refused code never blocks the booking: the reason is shown and the booker continues
 * at the undiscounted price.
 */
/**
 * Stable id so the field can point at the message. Fixed rather than generated: this section
 * appears once per booking step, and a generated id would need threading through two
 * components to say the same thing.
 */
const MESSAGE_ID = 'promotion-code-message'

const PromotionCodeEntry = ({
  cartID,
  cartKey = cartID,
  cartItems = [],
  code,
  disabled,
  errorCode,
  onChange
}: PromotionCodeEntryProps) => {
  const classes = useStyles()

  const { offers, loading: loadingOffers } = useAdvertisedOffers(cartID)
  const verdict = usePromotionEvaluation(cartID, code, cartKey)
  const pending = code.trim()
  const displayCode = lang === 'fa' ? `\u2066${pending}\u2069` : pending

  // Picking an offer IS entering its code — the advertised surface carries only
  // shared-code offers, so there is nothing else to select. Re-picking the selected one
  // clears it, which is the only way back to no code without emptying the field by hand.
  const handleOfferSelect = (offer: AdvertisedPromotion) => {
    onChange(pending === offer.code ? '' : offer.code)
  }

  const message = verdictMessage(verdict, errorCode)
  const noCodeSaving = /^0+$/.test(verdict.appliedForCode?.discountMinor ?? '')
  const appliedMessage = verdict.appliedForCode
    ? getTranslate(
        noCodeSaving
          ? 'Code {code} was accepted, but did not reduce the price.'
          : 'Discount with code {code} applied.',
        { code: displayCode }
      )
    : ''

  // Focus follows the swap — but only when the swap actually destroyed it.
  //
  // Applying an offer unmounts CodeField, including the Apply button the booker just
  // pressed; Remove unmounts AppliedOffer the same way. React removes the focused element,
  // focus falls to <body>, and the next Tab restarts from the top of the document,
  // mid-booking. A mouse user never notices; a keyboard or screen-reader user is put back at
  // the page header.
  //
  // GUARDED ON FOCUS ACTUALLY BEING LOST, not merely on the branch changing. Re-picking an
  // already-selected offer clears the code (see handleOfferSelect), which flips this flag
  // while the control the booker is operating stays mounted — so an unguarded version yanks
  // focus out of the offer list in response to activating it. document.activeElement is read
  // inside the effect, after the DOM commit, so a destroyed element has already fallen to
  // <body> by the time this runs.
  //
  // The apply direction needs no such guard: applying always destroys the pressed button.
  //
  // ON TRANSITION ONLY, and the seeding below is DEFENSIVE rather than load-bearing — said
  // plainly because an earlier version of this comment claimed otherwise. That state is
  // currently UNREACHABLE: usePromotionEvaluation holds checkedCode in useState with no
  // initial value and sets it only inside check(), so an offer is never applied on first
  // render. The ref is still seeded from the initial state rather than hardcoded false,
  // because the day that hook gains seeded state the difference stops being cosmetic. A
  // mutation test confirms nothing exercises it today.
  const isApplied = Boolean(verdict.appliedForCode)
  const wasApplied = useRef(isApplied)
  const sectionRef = useRef<HTMLElement>(null)
  const appliedRowRef = useRef<HTMLDivElement>(null)
  const removeButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (isApplied === wasApplied.current) {
      return
    }
    wasApplied.current = isApplied

    if (isApplied) {
      // The Remove BUTTON, not the row. It is a real control with its own accessible name,
      // it is already in the tab order so traversal continues naturally, and focusing it
      // announces "Remove, button" — a position, rather than a second reading of the outcome
      // the live region below is already speaking. The row is the fallback for when Remove
      // is disabled and therefore cannot take focus.
      if (removeButtonRef.current && !removeButtonRef.current.disabled) {
        removeButtonRef.current.focus()
      } else {
        appliedRowRef.current?.focus()
      }
      return
    }

    const focusWasLost =
      !document.activeElement || document.activeElement === document.body
    if (!focusWasLost) {
      return
    }

    // Scoped to THIS section, not the document. wbs-ng is an embeddable widget (src/embed.tsx
    // mounts it into a host page), so two instances on one page would put two
    // #promotion-code inputs in one document and a document-wide lookup would return the
    // first — silently focusing the other widget's field.
    //
    // The instanceof check is not defensive padding: the id reaches the input through
    // InputWithLabel's prop spread, which is unverified library behaviour at this pin. If an
    // upgrade moves it to the wrapper, .focus() on a div is a silent no-op — the same class
    // of failure as the ref that started all this.
    const field = sectionRef.current?.querySelector(`#${CODE_FIELD_ID}`)
    if (field instanceof HTMLInputElement) {
      field.focus()
    }
  }, [isApplied])

  return (
    <section
      ref={sectionRef}
      className={classes.card}
      aria-label={getTranslate('Promotion code')}>
      <CardHeading />
      <OfferList
        offers={offers}
        selectedCode={pending}
        disabled={disabled}
        loading={loadingOffers}
        onSelect={handleOfferSelect}
      />

      {/* Two states, never both. An editable input beside the word "applied" invites an
          edit that silently invalidates the verdict shown next to it, and Remove is the
          way back — clearing the field by hand still works and reaches the same place. */}
      {verdict.appliedForCode ? (
        <AppliedOffer
          rootRef={appliedRowRef}
          removeRef={removeButtonRef}
          applied={verdict.appliedForCode}
          message={appliedMessage}
          totalDiscountMinor={verdict.evaluation?.totalDiscountMinor}
          totalCurrency={verdict.evaluation?.currency}
          cartItems={cartItems}
          disabled={disabled}
          onRemove={() => onChange('')}
        />
      ) : (
        <CodeField
          code={code}
          disabled={disabled}
          checking={verdict.checking}
          // The resting prompt only. A refusal moved OUT of helper text and into the alert
          // row below, so it can be announced — see there.
          helperText={message.isError ? '' : message.text}
          error={message.isError}
          // The field points AT the message, which helper text never did — verified:
          // aria-describedby appears nowhere in @adotel/components' runtime bundle, so the
          // association this restores is one the component never had rather than one this
          // change removed. It matters more now than it would have: the message sits below
          // the whole row including the button, so the visual proximity that used to imply
          // the relationship is weaker too.
          //
          // aria-describedby rather than aria-errormessage. The latter is the semantically
          // precise answer and is only honoured alongside aria-invalid, with partial support
          // across NVDA/JAWS/VoiceOver; describedby is universal and does the job.
          messageId={message.isError ? MESSAGE_ID : undefined}
          onChange={onChange}
          onApply={() => verdict.check(code)}
        />
      )}

      {/* The refusal, announced rather than only rendered.

          It used to ride as the input's helper text. Helper text reaches assistive tech only
          through aria-describedby, which is read when focus ENTERS the field — and the
          guest's focus is on the Apply button they just pressed. So a screen-reader user
          could press Apply, be told nothing, and be left deciding whether to continue
          against silence. AppliedOffer already wraps the success case in role="status"; this
          is the missing other half.

          Lost in the port: admin-panel routes both failure states through a PromotionVerdict
          component carrying role="alert", whose own comment gives the reason — "the booker is
          waiting on this answer before deciding whether to continue". This app dropped that
          component in favour of the input's helper text, and the live region went with it. On
          the app the BOOKER actually uses, which is where that reasoning applies hardest.

          MOVED out of helper text rather than duplicated into a hidden region. The hidden
          copy was written first and put the same sentence on the page twice, which broke ten
          existing assertions that reasonably expected to find it once — a fair warning that
          two nodes saying one thing is a worse answer than one node saying it audibly.

          Errors only. The resting state is a standing prompt, and a live region that speaks
          on mount teaches a listener to ignore it before it says anything worth hearing. */}
      {/* The in-flight state, announced. Polite, and visually hidden.
          
          The Apply button carries aria-busy while checking, and the comment there claims it
          "says which moment that is". It does not: aria-busy is a hint, and no mainstream
          screen reader announces a change to it. The only other signal is a CircularProgress
          with no accessible name. So pressing Apply produced silence until the verdict
          landed — on a slow connection, unbounded silence after an explicit action, which is
          the same defect this MR set out to fix, one step earlier in the same interaction.
          
          Polite rather than assertive: the guest asked for this and is waiting, but it is
          progress, not an answer, and it must not interrupt anything. */}
      {/* ONE persistent live region, three states.
          
          DUPLICATED here, deliberately, where the refusal was MOVED — and the difference is
          the point rather than an inconsistency. A refusal's visible home was the field's
          helper text, which carries no independent value, so it could move wholesale to one
          visible alert node. "{name} applied" is the substance of AppliedOffer and has to
          stay on screen, so a reliable announcement can only be a second, hidden copy.
          
          That is the standard split and the two nodes are not saying the same thing to the
          same consumer: this region announces the CHANGE, the visible row is CONTENT a
          screen-reader user reads while browsing. The cost is that getByText now finds two
          matches for that string — the tests say so explicitly rather than quietly using
          getAllByText.
          
          Persistent is the other half of the point. A region inserted into the DOM already containing its text
          is commonly not announced at all — which is what AppliedOffer's own role="status"
          was doing, since it mounts pre-populated. This node is always present and only its
          contents change, which is the shape screen readers actually announce.
          
          Polite, not assertive: the booker asked for this and is waiting on it, but none of
          it should interrupt anything already being spoken. */}
      <div role="status" className={classes.announcement}>
        {verdict.checking ? getTranslate('Checking the code…') : appliedMessage}
      </div>

      {message.isError && (
        <Typography
          id={MESSAGE_ID}
          variant="body2"
          className={classes.refusal}
          role="alert">
          {(verdict.refusal ||
            (verdict.evaluation && !verdict.appliedForCode)) && (
            <strong>{getTranslate('Code not applied')}. </strong>
          )}
          {message.text}
        </Typography>
      )}
    </section>
  )
}

export default PromotionCodeEntry
